import { z } from "zod";
import { defineAd } from "../core/ad-definition";
import { adlandAPI } from "../services/adland.api";

/**
 * An X account as the creative — a person or a brand, rather than a thing they
 * posted.
 *
 * ── Why this is its own type and not a link ──────────────────────────────────
 *
 * `https://x.com/someone` resolves perfectly well as a `link`: the page carries
 * Open Graph tags and the preview would render. What it would render is a
 * generic link card — a banner image and "Name (@handle) on X" as a title — for
 * something that is not a page, it is an account. The card an account deserves
 * is an avatar, a handle and a follower count, which is the same card the
 * Farcaster profile type already draws.
 *
 * ── Why a handle rather than a numeric ID ────────────────────────────────────
 *
 * The opposite choice from `farcasterProfile`, which stores an `fid` because a
 * Farcaster handle is rentable and an fid is forever. X has no such stable
 * public ID in front of a person: nobody knows their own numeric ID, nobody
 * pastes one, and an ad whose data field is `1503851270` cannot be read by the
 * human who placed it. A handle can change owner, and that is the trade — the
 * metadata snapshot below is what pins down who was meant at the time.
 *
 * The canonical casing comes back from the API rather than from the paste, so
 * `@VitalikButerin` and `@vitalikbuterin` store the same ad.
 */
export const xProfileAd = defineAd({
  type: "xProfile",
  data: z.object({
    /** A profile URL or a bare `@handle`; normalised by the API's lookup. */
    handle: z.string().nonempty("Handle is required"),
  }),
  metadata: z.object({
    username: z.string().optional(),
    name: z.string().optional(),
    url: z.string().optional(),
    bio: z.string().optional(),
    pfpUrl: z.string().optional(),
    bannerUrl: z.string().optional(),
    /** A snapshot, not a live figure. See `XProfileMetadata`. */
    followers: z.number().optional(),
    following: z.number().optional(),
    posts: z.number().optional(),
    verified: z.boolean().optional(),
    /** ISO 8601. The join date — a profile's only age signal. */
    createdAt: z.string().optional(),
  }),
  async verify({ handle }) {
    const res = await adlandAPI.verify.xProfile({ url: handle });
    if (!res.verified) {
      throw new Error(
        "That account could not be read — it may not exist, or it may be protected",
      );
    }
  },
  async getMetadata({ handle }) {
    const profile = await adlandAPI.metadata.xProfile({ url: handle });
    return {
      username: profile.username,
      name: profile.name,
      url: profile.url,
      // `null` is what the API returns for an empty bio; the schema is
      // `optional`, so undefined is the shape that round-trips through JSON
      // without leaving a null in the stored ad.
      bio: profile.bio ?? undefined,
      pfpUrl: profile.pfpUrl ?? undefined,
      bannerUrl: profile.bannerUrl ?? undefined,
      followers: profile.followers,
      following: profile.following,
      posts: profile.posts,
      verified: profile.verified,
      createdAt: profile.createdAt ?? undefined,
    };
  },
});

export type XProfileAdData = z.infer<typeof xProfileAd.data>;
export type XProfileAdMetadata = z.infer<
  NonNullable<typeof xProfileAd.metadata>
>;
export type XProfileAd = {
  type: "xProfile";
  data: XProfileAdData;
  metadata: XProfileAdMetadata;
};
